import {
    Modal,
    Box,
    Typography,
    IconButton,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    CircularProgress,
    useTheme,
    styled,
    tableCellClasses
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { tokens } from '../../theme';

const TeacherReviewsModal = ({ open, handleClose, reviewSessions, loading, refreshSessions }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    console.log('Review Sessions:', reviewSessions);
    
    const StyledTableCell = styled(TableCell)(({ theme }) => ({
        [`&.${tableCellClasses.head}`]: {
            backgroundColor: colors.primary[300],
            color: theme.palette.common.white,
            fontSize: 14,
            fontWeight: 'bold',
        },
        [`&.${tableCellClasses.body}`]: {
            fontSize: 14,
        },
    }));
    
    const StyledTableRow = styled(TableRow)(({ theme }) => ({
        '&:nth-of-type(odd)': {
            backgroundColor: theme.palette.action.hover,
        },
        '&:last-child td, &:last-child th': {
            border: 0,
        },
        '&:hover': {
            backgroundColor: theme.palette.action.selected,
        },
    }));

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '80%',
        maxWidth: '900px',
        maxHeight: '80vh',
        bgcolor: colors.primary[600],
        border: `2px solid ${colors.primary[400]}`,
        boxShadow: 24,
        p: 4,
        borderRadius: 2,
        overflow: 'auto',
    };

    const getStatusColor = (expiresAt) => {
        if (!expiresAt) return 'default';
        const now = new Date();
        const expiry = new Date(expiresAt);
        if (expiry < now) return 'error';
        const daysUntilExpiry = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
        if (daysUntilExpiry <= 7) return 'warning';
        return 'success';
    };
    
    const getExpiryText = (expiresAt) => {
        if (!expiresAt) return 'No expiry';
        const now = new Date();
        const expiry = new Date(expiresAt);
        if (expiry < now) return 'Expired';
        const daysUntilExpiry = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
        if (daysUntilExpiry === 0) return 'Expires today';
        if (daysUntilExpiry === 1) return 'Expires tomorrow';
        if (daysUntilExpiry <= 7) return `${daysUntilExpiry} days left`;
        return `${daysUntilExpiry} days`;
    };
    
    const getProgressColor = (percentage) => {
        if (percentage === 100) return 'success';
        if (percentage >= 50) return 'info';
        if (percentage > 0) return 'warning';
        return 'default';
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="teacher-reviews-modal-title"
            aria-describedby="teacher-reviews-modal-description"
        >
            <Box sx={style}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography 
                        id="teacher-reviews-modal-title" 
                        variant="h4" 
                        component="h2"
                        color={colors.primary[100]}
                    >
                        Teacher Review Sessions
                    </Typography>
                    <Box display="flex" gap={1}>
                        <IconButton 
                            onClick={refreshSessions}
                            title="Refresh"
                            disabled={loading}
                        >
                            <RefreshIcon />
                        </IconButton>
                        <IconButton onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>

                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                    </Box>
                ) : reviewSessions.length === 0 ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <Typography variant="h6" color={colors.grey[300]}>
                            No review sessions available
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>Session Title</StyledTableCell>
                                    <StyledTableCell align="center">Total Questions</StyledTableCell>
                                    <StyledTableCell align="center">Reviewed</StyledTableCell>
                                    <StyledTableCell align="center">Progress</StyledTableCell>
                                    <StyledTableCell align="center">Expires</StyledTableCell>
                                    <StyledTableCell align="center">Action</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reviewSessions.map((session, index) => {
                                    const progress = session.my_progress || {};
                                    const progressPercentage = progress.progress_percentage || 0;
                                    
                                    return (
                                        <StyledTableRow key={session.id || index}>
                                            <StyledTableCell component="th" scope="row">
                                                <Typography variant="body2" fontWeight="medium">
                                                    {session.title || 'Untitled Session'}
                                                </Typography>
                                                {session.instructions && (
                                                    <Typography variant="caption" color="text.secondary" 
                                                        sx={{ display: 'block', mt: 0.5, maxWidth: 300 }}>
                                                        {session.instructions}
                                                    </Typography>
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                {session.total_questions || 0}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                <Box>
                                                    <Typography variant="body2">
                                                        {progress.reviewed_items || 0}/{progress.total_items || 0}
                                                    </Typography>
                                                    {progress.remaining_items > 0 && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            ({progress.remaining_items} remaining)
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                                                    <Chip 
                                                        label={`${progressPercentage.toFixed(2)}%`}
                                                        color={getProgressColor(progressPercentage)}
                                                        size="small"
                                                        variant={progressPercentage === 100 ? "filled" : "outlined"}
                                                    />
                                                </Box>
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                <Chip 
                                                    label={getExpiryText(session.expires_at)}
                                                    color={getStatusColor(session.expires_at)}
                                                    size="small"
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    color={progressPercentage === 100 ? "success" : "primary"}
                                                    onClick={() => {
                                                        // Open review page in new tab
                                                        window.open(`/teacher-review/${session.id}`, '_blank');
                                                    }}
                                                >
                                                    {progressPercentage === 100 ? 'View / Edit' : 'Review'}
                                                </Button>
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {!loading && reviewSessions.length > 0 && (
                    <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                            Total: {reviewSessions.length} session(s)
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Pending Reviews: {reviewSessions.reduce((acc, s) => 
                                acc + (s.my_progress?.remaining_items || 0), 0
                            )}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Modal>
    );
};

export default TeacherReviewsModal;